import React, {forwardRef, useImperativeHandle, useState} from "react"
import Papa from "papaparse"

import Flex from "@irati/ui/src/Flex"
import Icon from "@irati/ui/src/Icon"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import {useTheme} from "@irati/ui/src/global/useTheme"

import teamService from "@irati/identity/services/teams"
import useAuth from "@irati/auth/helpers/auth/useAuth"
import {TeamState, User} from "@irati/types"
import usersService from "@irati/identity/services/users"
import makeCSV from "@irati/utils/csv/makeCSV"
import Loading from "@irati/ui/src/Loading"
import {DownloadCsvText} from "../Users/AddUsers/AddPeopleCSV/styled"

const DownloadUsersExport = forwardRef((_props, ref) => {
  const theme = useTheme()
  const auth = useAuth()
  const {formatMessage} = useIntl()
  const [load, setLoad] = useState(null)

  const getUserList = (users: User[] = [], cursor?: string, timesRequested = 1): Promise<User[]> => {
    const params = {
      businessId: auth.business.uuid,
      cursor,
      includeIntegrations: false,
      enabled: true
    }
    const variables = params

    return usersService.getUsersForBusiness(variables, {fetchPolicy: "network-only"}).then(({results, cursor: newCursor, total}) => {
      const newUsers = results
      const allUsers = users.concat(newUsers)
      if (newCursor) {
        return getUserList(allUsers, newCursor, timesRequested + 1)
      }
      return allUsers
    })
  }

  useImperativeHandle(ref, () => ({
    download() {
      downloadCsv()
    }
  }))

  const downloadCsv = () => {
    const csvLoad = teamService.list(auth.business.uuid, {state: TeamState.Generic}, {fetchPolicy: "network-only"}).then((teams) => {
      return getUserList().then((users: User[]) => {
        const headers = [
          formatMessage({id: "admin.components.downloadUsersExport.headers.firstname"}),
          formatMessage({id: "admin.components.downloadUsersExport.headers.lastname"}),
          formatMessage({id: "admin.components.downloadUsersExport.headers.email"}),
          formatMessage({id: "admin.components.downloadUsersExport.headers.teams"})
        ]
        const data = users.map((user: User) => [
          user.firstName,
          user?.lastName,
          user.email,
          user?.teamIds
            .map((teamId) => teams.find((t) => t.uuid === teamId)?.teamName)
            .filter((t) => !!t)
            .join(", ")
        ])
        const csv = Papa.unparse({fields: headers, data})
        makeCSV(csv, "users.csv")
      })
    })
    setLoad(csvLoad)
  }

  return (
    <Loading load={load}>
      <DownloadCsvText onClick={downloadCsv}>
        <Flex alignItems="center">
          <Icon icon="fileDownload" color={theme.colors.perkbox.blue} marginRight={1} />
          <FormattedMessage id="admin.components.downloadUsersExport.text" />
        </Flex>
      </DownloadCsvText>
    </Loading>
  )
})

DownloadUsersExport.displayName = "DownloadUsersExport"

export default DownloadUsersExport
